import { notify } from "@/components/common/Notification";

function formatFieldLabel(field: string): string {
  return field
    .split(".")
    .map(part => part.replace(/_/g, " "))
    .join(" · ");
}

function extractErrorDetailMessages(raw: string): string[] {
  const messages: string[] = [];
  const detailPattern = /ErrorDetail\(string='([^']+)'/g;
  let match: RegExpExecArray | null;

  while ((match = detailPattern.exec(raw)) !== null) {
    if (match[1]?.trim()) {
      messages.push(match[1].trim());
    }
  }

  return messages;
}

function parseFieldValidationMessages(field: string, value: unknown): string[] {
  const fieldLabel = formatFieldLabel(field);

  if (value === null || value === undefined) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(item => parseFieldValidationMessages(field, item));
  }

  if (typeof value === "string") {
    const detailMessages = extractErrorDetailMessages(value);
    if (detailMessages.length > 0) {
      return detailMessages.map(message => `${fieldLabel}: ${message}`);
    }

    const trimmed = value.trim();
    return trimmed ? [`${fieldLabel}: ${trimmed}`] : [];
  }

  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).flatMap(
      ([nestedField, nestedValue]) =>
        parseFieldValidationMessages(`${field}.${nestedField}`, nestedValue)
    );
  }

  return [`${fieldLabel}: ${String(value)}`];
}

export function extractApiValidationMessages(data: unknown): string[] {
  if (!data || typeof data !== "object") {
    return [];
  }

  const record = data as Record<string, unknown>;
  const error = record.error;
  const messages: string[] = [];

  if (error && typeof error === "object") {
    const errorRecord = error as Record<string, unknown>;
    const details = errorRecord.details;

    if (details && typeof details === "object") {
      messages.push(
        ...Object.entries(details as Record<string, unknown>).flatMap(
          ([field, value]) => parseFieldValidationMessages(field, value)
        )
      );
    }

    if (typeof errorRecord.message === "string" && errorRecord.message.trim()) {
      messages.unshift(errorRecord.message.trim());
    }
  }

  if (typeof record.message === "string" && record.message.trim()) {
    messages.push(record.message.trim());
  }

  return [...new Set(messages.filter(Boolean))];
}

export function notifyBillingApiErrors(
  data: unknown,
  fallback = "Failed to save configuration. Please try again."
): void {
  const messages = extractApiValidationMessages(data);

  if (messages.length === 0) {
    notify.error(fallback);
    return;
  }

  messages.forEach(message => notify.error(message));
}

export function extractBillingConfigurationId(data: unknown): string | null {
  if (!data || typeof data !== "object") {
    return null;
  }

  const record = data as Record<string, unknown>;
  const directCandidates = [
    record.id,
    record.billing_id,
    record.billing_configuration_id,
  ];

  for (const candidate of directCandidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  const nestedData = record.data;
  if (nestedData && typeof nestedData === "object") {
    const nestedRecord = nestedData as Record<string, unknown>;
    const nestedCandidates = [
      nestedRecord.id,
      nestedRecord.billing_id,
      nestedRecord.billing_configuration_id,
    ];

    for (const candidate of nestedCandidates) {
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }
  }

  return null;
}
